import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { ArrowLeft, ArrowRight, Award, Users, Building2, PenTool as Tool, Star, CheckCircle } from 'lucide-react';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';

const stats = [
  { number: 14, label: 'Years of Excellence' },
  { number: 2000, label: 'Projects Completed' },
  { number: 98, label: 'Client Satisfaction' }
];

const values = [
  {
    icon: Award,
    title: 'Licensed Excellence',
    description: 'Fully licensed and insured in both Westchester County, NY and Fairfield County, CT.'
  },
  {
    icon: Users,
    title: 'Client-Focused',
    description: 'Your vision and satisfaction are our top priorities. We listen, adapt, and deliver beyond expectations.'
  },
  {
    icon: Tool,
    title: 'Quality Craftsmanship',
    description: 'Our master builders bring decades of experience and attention to detail to every project.'
  },
  {
    icon: Building2,
    title: 'Full-Service Construction',
    description: 'From new construction to renovations, we handle projects of all sizes with equal dedication.'
  }
];

const milestones = [
  {
    year: '2010',
    title: 'Company Founded',
    description: 'Established operations in Westchester County, NY.'
  },
  {
    year: '2015',
    title: 'CT Expansion',
    description: 'Expanded services to Fairfield County, CT with full licensing.'
  },
  {
    year: '2024',
    title: 'Industry Leader',
    description: 'Leading construction services across NY and CT with a focus on excellence.'
  }
];

export function AboutPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>About Our Renovation Company | Westchester & Fairfield Counties</title>
        <meta name="description" content="Leading renovation company serving Westchester County, NY and Fairfield County, CT since 2010. Licensed and insured in both states, delivering excellence in home renovations." />
        <meta name="keywords" content="renovation company history, Westchester renovation, Fairfield renovation contractor, licensed renovation company" />
        <link rel="canonical" href="https://renovationwestchester.com/about" />
      </Helmet>

      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Home</span>
          </Link>

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-16 items-center mb-32"
          >
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-8">
                Renovating Excellence Across Two States
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Since 2010, we've been a leading renovation company serving both 
                Westchester County, NY and Fairfield County, CT. Fully licensed and 
                insured in both states, we bring unmatched expertise to every project.
              </p>
              <p className="text-zinc-400 mb-12 leading-relaxed">
                From kitchen renovations to complete home makeovers, our team 
                delivers exceptional results with a focus on quality, efficiency, and 
                client satisfaction.
              </p>
              <Link 
                to="/consultation" 
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900 group transform hover:scale-105 transition-all duration-300"
              >
                <span className="text-sm tracking-wider">Start Your Project</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1541976590-713941681591?auto=format&fit=crop&q=80"
                alt="Construction Excellence"
                className="w-full aspect-[4/3] object-cover rounded-xl"
              />
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-32">
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-light text-yellow-400 mb-4">
                  <CountUp 
                    end={stat.number} 
                    duration={2.5} 
                    suffix={stat.label === 'Client Satisfaction' ? '%' : '+'} 
                    enableScrollSpy
                    scrollSpyOnce
                  />
                </div>
                <p className="text-zinc-400 text-sm tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-light text-zinc-100 mb-4">Our Values</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              These core principles guide every project we undertake across Westchester 
              and Fairfield counties, ensuring consistent quality and client satisfaction.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-zinc-800/30 p-8 text-center rounded-xl transform hover:scale-105 transition-all duration-300"
              >
                <value.icon className="h-10 w-10 text-yellow-400 mx-auto mb-6" />
                <h3 className="text-xl text-zinc-100 mb-4">{value.title}</h3>
                <p className="text-zinc-400">{value.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mb-32">
            <motion.h2 
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-3xl font-light text-zinc-100 mb-16 text-center"
            >
              Our Journey
            </motion.h2>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div 
                  key={index}
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative pl-8 border-l border-yellow-400"
                >
                  <div className="absolute left-0 top-0 w-4 h-4 bg-yellow-400 rounded-full -translate-x-[9px]" />
                  <div className="bg-zinc-800/30 p-8 rounded-xl transform hover:scale-[1.02] transition-all duration-300">
                    <div className="text-yellow-400 text-sm tracking-wider mb-2">{milestone.year}</div>
                    <h3 className="text-xl text-zinc-100 mb-4">{milestone.title}</h3>
                    <p className="text-zinc-400">{milestone.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-32"
          >
            <div className="bg-zinc-800/30 p-12 rounded-xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-light text-zinc-100 mb-4">Our Owner</h2>
                <div className="w-24 h-1 bg-yellow-400 mx-auto mb-8"></div>
              </div>
              
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h3 className="text-2xl text-yellow-400 mb-4">Mat Gurdak</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    With over 14 years of experience in the construction industry, Mat Gurdak founded 
                    Golden Touch Contracting with a vision to deliver exceptional craftsmanship and 
                    unparalleled service to homeowners throughout Westchester County, NY and Fairfield 
                    County, CT. His commitment to quality and attention to detail has established 
                    Golden Touch as a trusted name in renovation and construction services.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-8 text-center">
                  <div className="bg-zinc-700/30 p-6 rounded-lg">
                    <div className="text-yellow-400 text-2xl font-light mb-2">14+</div>
                    <div className="text-zinc-400 text-sm">Years Experience</div>
                  </div>
                  <div className="bg-zinc-700/30 p-6 rounded-lg">
                    <div className="text-yellow-400 text-2xl font-light mb-2">2000+</div>
                    <div className="text-zinc-400 text-sm">Projects Completed</div>
                  </div>
                  <div className="bg-zinc-700/30 p-6 rounded-lg">
                    <div className="text-yellow-400 text-2xl font-light mb-2">98%</div>
                    <div className="text-zinc-400 text-sm">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-32"
          >
            <div className="bg-zinc-800/30 p-12 rounded-xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-light text-zinc-100 mb-4">Mat's Philosophy & Approach</h2>
                <div className="w-24 h-1 bg-yellow-400 mx-auto mb-8"></div>
              </div>
              
              <div className="max-w-4xl mx-auto space-y-8">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <h3 className="text-xl text-yellow-400 mb-4">Craftsmanship First</h3>
                    <p className="text-zinc-400 leading-relaxed mb-6">
                      Mat believes that true craftsmanship goes beyond just completing a project—it's about 
                      creating something that homeowners will love for decades to come. Every detail matters, 
                      from the initial consultation to the final walkthrough.
                    </p>
                    <p className="text-zinc-400 leading-relaxed">
                      His hands-on approach ensures that every project receives personal attention, whether 
                      it's a simple bathroom update or a complete home renovation.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl text-yellow-400 mb-4">Building Relationships</h3>
                    <p className="text-zinc-400 leading-relaxed mb-6">
                      What sets Mat apart is his commitment to building lasting relationships with clients. 
                      Many customers return for additional projects and refer friends and family, a testament 
                      to the trust and quality he delivers.
                    </p>
                    <p className="text-zinc-400 leading-relaxed">
                      Mat takes pride in being accessible throughout the entire process, ensuring clear 
                      communication and transparency from start to finish.
                    </p>
                  </div>
                </div>
                
                <div className="border-t border-zinc-700 pt-8">
                  <div className="text-center">
                    <h3 className="text-xl text-yellow-400 mb-4">Local Expertise</h3>
                    <p className="text-zinc-400 leading-relaxed max-w-3xl mx-auto">
                      Having worked extensively throughout Westchester County, NY and Fairfield County, CT, 
                      Mat understands the unique architectural styles, local building codes, and permit 
                      requirements of both regions. This local expertise ensures smooth project execution 
                      and helps avoid common pitfalls that can delay or complicate renovations.
                    </p>
                  </div>
                </div>
                
                <div className="bg-zinc-700/30 p-8 rounded-lg">
                  <div className="text-center">
                    <blockquote className="text-lg text-zinc-300 italic mb-4">
                      "Every home has potential. My job is to help homeowners see that potential 
                      and bring it to life in a way that fits their lifestyle and budget."
                    </blockquote>
                    <cite className="text-yellow-400 text-sm">— Mat Gurdak, Founder</cite>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
